import React, { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { ArrowUp, PlusCircle, ClockHistory, Trash, ArrowLeft, Share, Check } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { TradingContext } from '../../contexts/TradingContext';
import { AppStateContext } from '../../contexts/AppStateContext';
import { StreamsContext } from '../../contexts/StreamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { hasTradeFeatureAccess } from '../../utils/authAccess';
import { BannerContext } from '../../contexts/BannerContext';
import { PusherContext } from '../../providers/PusherProvider';
import { parseQuoteLast } from '../../utils/quoteNetChangePct';
import useAtr from '../../hooks/useAtr';
import useIsMobile from '../../hooks/useIsMobile';
import { AI_CHAT_FONT_SIZE, AI_CHAT_LINE_HEIGHT, createAiChatMarkdownComponents } from './aiChatMarkdownComponents';
import {
  CHAT_POLL_MS,
  INTERRUPTED_REPLY,
  isSessionProcessing,
  readActiveSessionId,
  writeActiveSessionId,
} from './aiChatSession';
import {
  applyPartialToMessages,
  chatStreamEventContent,
  chatStreamEventSessionId,
  consumeSseResponse,
  isEventStreamResponse,
  mergeMessagesWithPartial,
  mergeServerMessagesWithPending,
  pickLongerPartial,
  sessionPartialContent,
} from './aiChatStream';
import {
  buildChatShareSnapshot,
  chatShareTitle,
  createChatShare,
} from './aiChatShare';
import AIChatTranscript, { assistantBubbleColors } from './AIChatTranscript';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Transcript canvas, darker than the app background so bubbles stay easy to separate. */
function chatCanvasBackground(bgPrimary) {
  const hex = typeof bgPrimary === 'string' ? bgPrimary.trim() : '';
  const match = hex.match(/^#([0-9a-f]{6})$/i);
  if (!match) return '#07090F';
  const n = parseInt(match[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance < 150 ? '#07090F' : '#E2E6EE';
}

const SUGGESTED_PROMPTS = [
  'What are my biggest losing positions right now?',
  'What mistakes have I been making recently?',
  'Set up a new buy bracket order on my chart',
  'What trading rules am I breaking the most often?',
];

const LOADING_PHRASES = [
  'Working on it...',
  'Crunching the data...',
  'Pulling it together...',
  'Almost there...',
];

const LOADING_PHRASE_INTERVAL_MS = 8000;
const CHAT_REQUEST_TIMEOUT_MS = 30_000;

const fetchWithAuth = (path, options = {}) => {
  const token = localStorage.getItem('authToken');
  return fetch(`${BACKEND_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
};

const fetchWithAuthTimeout = (path, options = {}, timeoutMs = CHAT_REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return fetchWithAuth(path, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timeoutId));
};

const PAGE_CONTENT_MAX_WIDTH = 680;

/**
 * Chat history reads as a list of titles, the way every other assistant sidebar does.
 * Dates live in group headers rather than on every row; the exact date and message
 * count are secondary, so they surface on hover/focus instead of taking a second line.
 */
const SESSION_GROUP_ORDER = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'Previous 7 days' },
  { key: 'month', label: 'Previous 30 days' },
  { key: 'older', label: 'Older' },
];

function calendarDaysAgo(value, now) {
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;
  then.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - then.getTime()) / 86_400_000);
}

function sessionGroupKey(value, now) {
  const days = calendarDaysAgo(value, now);
  if (days == null) return 'older';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return 'week';
  if (days < 30) return 'month';
  return 'older';
}

export function groupSessionsByRecency(sessions, now = Date.now()) {
  const byKey = new Map();
  for (const session of sessions || []) {
    const key = sessionGroupKey(session?.updated_at, now);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(session);
  }
  return SESSION_GROUP_ORDER
    .filter((group) => byKey.get(group.key)?.length)
    .map((group) => ({ ...group, sessions: byKey.get(group.key) }));
}

export function formatSessionMeta(session, now = Date.now()) {
  const date = new Date(session?.updated_at);
  const count = Number(session?.message_count) || 0;
  const messages = `${count} msg${count === 1 ? '' : 's'}`;
  if (Number.isNaN(date.getTime())) return messages;
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  const day = date.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
  return `${day} · ${messages}`;
}

/**
 * Reusable chat UI for PrecisionAI — fills its container, no fixed positioning.
 * Used in the desktop left sidebar tab, dashboard page, and mobile PrecisionAI page.
 * @param {boolean} [pageLayout] — constrain and center messages + input (dashboard full-page)
 */
const HISTORY_SIDEBAR_WIDTH = 280;

const AIChatPanel = ({ pageLayout = false }) => {
  const { colors } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const useDesktopHistorySidebar = pageLayout && !isMobile;
  const [view, setView] = useState('chat'); // 'chat' | 'history' (full-screen on mobile / sidebar tab)
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // Touch has no hover, so those rows keep their meta and delete affordance on screen.
  const [activeSessionRow, setActiveSessionRow] = useState(null);
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [hydrating, setHydrating] = useState(true);
  const [shareCopied, setShareCopied] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const messagesRef = useRef([]);
  const stickToBottomRef = useRef(true);
  const awaitingAckRef = useRef(false);
  const transcriptEpochRef = useRef(0);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);
  const isLoadingRef = useRef(false);
  const userActedRef = useRef(false);
  const streamTextRef = useRef('');

  const { selectedAccount, isPaperTrading, selectedTickerDetails } = useContext(TradingContext);
  const { selectedTicker } = useContext(AppStateContext);
  const { quotesBySymbol } = useContext(StreamsContext);
  const { user } = useContext(AuthContext);
  const suggestedPrompts = hasTradeFeatureAccess(user)
    ? SUGGESTED_PROMPTS
    : SUGGESTED_PROMPTS.filter((prompt) => prompt !== 'Set up a new buy bracket order on my chart');
  const { showMessage } = useContext(BannerContext);
  const pusher = useContext(PusherContext);
  const accountId = selectedAccount?.AccountID;
  const paperTrading = selectedAccount?.isPaperTrading ?? isPaperTrading;
  const currentPrice = parseQuoteLast(quotesBySymbol?.[selectedTicker]);
  const atrDistance = useAtr(100, 1); // 1x ATR, the unit the AI uses for default bracket distances
  // Dollars per point. The chart quantity selector multiplies stop distance by this
  // (PriceFormat.PointValue), so a futures draft has to use the same number.
  const detailsMatchTicker = selectedTickerDetails?.Symbol
    && selectedTicker
    && String(selectedTickerDetails.Symbol).toUpperCase() === String(selectedTicker).toUpperCase();
  const pointValue = detailsMatchTicker ? Number(selectedTickerDetails?.PriceFormat?.PointValue) : NaN;
  const markdownComponents = useMemo(() => createAiChatMarkdownComponents(colors), [colors]);
  const sessionGroups = useMemo(() => groupSessionsByRecency(sessions), [sessions]);
  messagesRef.current = messages;

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const adoptSession = useCallback((data) => {
    if (!data?.id) return;
    writeActiveSessionId(data.id);
    const messagesFromServer = Array.isArray(data.messages) ? data.messages : [];
    const interrupted = data.status === 'processing' && !isSessionProcessing(data);
    const processing = !interrupted && isSessionProcessing(data);
    const merged = mergeServerMessagesWithPending(messagesFromServer, messagesRef.current);
    const keepTail = awaitingAckRef.current && merged.retainedPending;
    if (awaitingAckRef.current && !merged.retainedPending) awaitingAckRef.current = false;
    sessionIdRef.current = data.id;
    const stillLoading = processing || keepTail;
    isLoadingRef.current = stillLoading;
    setSessionId(data.id);
    setMessages((prev) => {
      const base = keepTail
        ? mergeServerMessagesWithPending(messagesFromServer, prev).messages
        : messagesFromServer;
      if (interrupted) {
        streamTextRef.current = '';
        return [...base, { role: 'assistant', content: INTERRUPTED_REPLY }];
      }
      if (!processing) {
        streamTextRef.current = '';
        return mergeMessagesWithPartial(base, '', { processing: false });
      }
      const incomingPartial = sessionPartialContent(data);
      const localLast = prev[prev.length - 1];
      const localPartial = localLast?.role === 'assistant' && localLast.streaming
        ? (localLast.content || '')
        : '';
      const partial = pickLongerPartial(
        pickLongerPartial(incomingPartial, localPartial),
        streamTextRef.current,
      );
      streamTextRef.current = partial;
      return mergeMessagesWithPartial(base, partial, { processing: true });
    });
    setIsLoading(stillLoading);
  }, []);

  const applyPartial = useCallback((text) => {
    if (!text) return;
    streamTextRef.current = text;
    setMessages((prev) => applyPartialToMessages(prev, text));
  }, []);

  const fetchSession = useCallback(async (id) => {
    const res = await fetchWithAuth(`/api/chat/sessions/${id}`);
    if (!res.ok) return null;
    return res.json();
  }, []);

  const pageContentStyle = pageLayout
    ? {
        maxWidth: PAGE_CONTENT_MAX_WIDTH,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingLeft: 16,
        paddingRight: 16,
        boxSizing: 'border-box',
      }
    : null;

  const scrollMessagesToBottom = useCallback((smooth = false) => {
    const container = messagesScrollRef.current;
    if (!container) return;
    const run = () => {
      container.scrollTop = container.scrollHeight;
    };
    if (smooth && typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    } else {
      run();
      requestAnimationFrame(run);
    }
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const container = messagesScrollRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 100;
  }, []);

  // Stay pinned when the user was already at the bottom. Measuring after the
  // new bubble is painted looks like they scrolled away, so the flag is updated
  // from the scroll handler instead.
  useEffect(() => {
    if (stickToBottomRef.current || messages.length <= 2) {
      scrollMessagesToBottom(messages.length > 2);
    }
  }, [messages, isLoading, scrollMessagesToBottom]);

  // Desktop sidebar only — avoid opening the keyboard when switching to the AI tab on mobile
  useEffect(() => {
    if (view === 'chat' && !isMobile) {
      inputRef.current?.focus();
    }
  }, [view, isMobile]);

  useEffect(() => {
    if (!isLoading) {
      setLoadingPhraseIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setLoadingPhraseIndex((i) => (i + 1) % LOADING_PHRASES.length);
    }, LOADING_PHRASE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoading]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await fetchWithAuth('/api/chat/sessions');
      if (res.ok) setSessions(await res.json());
    } catch (e) {
      console.error('[AIChatPanel] Failed to load sessions:', e);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (useDesktopHistorySidebar) {
      loadSessions();
    }
  }, [useDesktopHistorySidebar, loadSessions]);

  // Coming back to the page remounts this panel. Restore the chat that was open,
  // and any turn still running on the server, instead of starting blank.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      try {
        const storedId = readActiveSessionId();
        if (storedId && !userActedRef.current) {
          const session = await fetchSession(storedId);
          if (cancelled || userActedRef.current) return;
          if (session) {
            adoptSession(session);
            return;
          }
          writeActiveSessionId(null);
        }

        const res = await fetchWithAuth('/api/chat/sessions');
        if (!res.ok || cancelled || userActedRef.current) return;
        const list = await res.json();
        const processing = (Array.isArray(list) ? list : []).find((session) => isSessionProcessing(session));
        if (!processing || cancelled || userActedRef.current) return;
        const session = await fetchSession(processing.id);
        if (cancelled || userActedRef.current || !session) return;
        adoptSession(session);
      } catch (e) {
        console.error('[AIChatPanel] Failed to restore chat:', e);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, [adoptSession, fetchSession]);

  // Poll while a turn is in flight so a missed Pusher event still lands the reply.
  useEffect(() => {
    if (!isLoading || !sessionId) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const epoch = transcriptEpochRef.current;
        const session = await fetchSession(sessionId);
        if (cancelled || !session) return;
        if (epoch !== transcriptEpochRef.current) return;
        if (String(sessionIdRef.current) !== String(session.id)) return;
        if (isSessionProcessing(session) && !sessionPartialContent(session)) return;
        adoptSession(session);
      } catch (e) {
        console.error('[AIChatPanel] Failed to refresh in-progress chat:', e);
      }
    };

    tick();
    const id = setInterval(tick, CHAT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isLoading, sessionId, fetchSession, adoptSession]);

  useEffect(() => {
    if (!pusher || !user?.id) return undefined;

    const channelName = `user-${user.id}-chat`;
    let channel = pusher.channel(channelName);
    if (!channel) {
      channel = pusher.subscribe(channelName);
    }

    const onChatUpdated = (data) => {
      const incomingId = data?.sessionId ?? data?.session_id;
      if (incomingId == null) {
        const streamed = chatStreamEventContent(data, streamTextRef.current);
        if (streamed && isLoadingRef.current) applyPartial(streamed);
        return;
      }

      setSessions((prev) => prev.map((session) => (
        String(session.id) === String(incomingId)
          ? { ...session, status: data.status || session.status }
          : session
      )));

      const current = sessionIdRef.current;
      if (current && String(current) !== String(incomingId)) return;
      if (!current && !isLoadingRef.current) return;

      const streamed = chatStreamEventContent(data, streamTextRef.current);
      if (streamed) applyPartial(streamed);

      const finished = data.status && data.status !== 'processing';
      if (!finished && streamed) return;

      fetchSession(incomingId).then((session) => {
        if (!session) return;
        const latest = sessionIdRef.current;
        if (latest && String(latest) !== String(incomingId)) return;
        if (!latest && !isLoadingRef.current) return;
        adoptSession(session);
      }).catch((e) => {
        console.error('[AIChatPanel] Failed to apply chat update:', e);
      });
    };

    channel.bind('chat-updated', onChatUpdated);
    channel.bind('chat-delta', onChatUpdated);
    return () => {
      channel.unbind('chat-updated', onChatUpdated);
      channel.unbind('chat-delta', onChatUpdated);
    };
  }, [pusher, user?.id, fetchSession, adoptSession, applyPartial]);

  useEffect(() => {
    if (!isLoading || !sessionId) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    let streamed = '';

    const run = async () => {
      try {
        const res = await fetchWithAuth(`/api/chat/sessions/${sessionId}/stream`, {
          headers: { Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (cancelled || !res?.ok || !isEventStreamResponse(res)) return;
        await consumeSseResponse(res, {
          signal: controller.signal,
          onEvent: (event) => {
            if (cancelled) return;
            if (event.done || event.status === 'idle') {
              fetchSession(sessionId).then((session) => {
                if (!cancelled && session) adoptSession(session);
              });
              return;
            }
            streamed = chatStreamEventContent(event, streamed);
            applyPartial(streamed);
          },
        });
      } catch (e) {
        if (e?.name === 'AbortError' || cancelled) return;
      }
    };

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isLoading, sessionId, fetchSession, adoptSession, applyPartial]);

  const closeHistory = useCallback(() => {
    setView('chat');
  }, []);

  const openHistory = () => {
    loadSessions();
    setView('history');
  };

  const startNewChat = () => {
    userActedRef.current = true;
    writeActiveSessionId(null);
    sessionIdRef.current = null;
    isLoadingRef.current = false;
    streamTextRef.current = '';
    messagesRef.current = [];
    stickToBottomRef.current = true;
    awaitingAckRef.current = false;
    transcriptEpochRef.current += 1;
    setMessages([]);
    setSessionId(null);
    setInput('');
    setIsLoading(false);
    setView('chat');
  };

  const loadSession = async (session) => {
    userActedRef.current = true;
    try {
      const data = await fetchSession(session.id);
      if (!data) return;
      adoptSession(data);
      setView('chat');
      setTimeout(() => scrollMessagesToBottom(false), 50);
    } catch (e) {
      console.error('[AIChatPanel] Failed to load session:', e);
    }
  };

  const deleteSession = async (e, sessionIdToDelete) => {
    e.stopPropagation();
    try {
      await fetchWithAuth(`/api/chat/sessions/${sessionIdToDelete}`, { method: 'DELETE' });
      setSessions(prev => prev.filter(s => s.id !== sessionIdToDelete));
    } catch (e) {
      console.error('[AIChatPanel] Failed to delete session:', e);
    }
  };

  const copyShareLink = useCallback(async ({ id, title, messages: shareMessages }) => {
    const snapshot = buildChatShareSnapshot({ title, messages: shareMessages });
    try {
      const { url } = await createChatShare({
        sessionId: id,
        snapshot,
        request: fetchWithAuth,
      });
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
      showMessage?.('Share link copied', 'success');
    } catch (error) {
      console.error('[AIChatPanel] Failed to share chat:', error);
      showMessage?.(error.message || 'Could not copy a share link.', 'danger');
    }
  }, [showMessage]);

  const shareCurrentChat = useCallback(async () => {
    const current = sessions.find((session) => String(session.id) === String(sessionId));
    await copyShareLink({
      id: sessionId,
      title: chatShareTitle(current, messages),
      messages,
    });
  }, [copyShareLink, messages, sessionId, sessions]);

  const shareSessionFromHistory = async (e, session) => {
    e.stopPropagation();
    try {
      const data = String(session.id) === String(sessionId)
        ? { title: session.title, messages }
        : await fetchSession(session.id);
      if (!data) return;
      await copyShareLink({
        id: session.id,
        title: chatShareTitle(data, data.messages || messages),
        messages: data.messages || messages,
      });
    } catch (error) {
      console.error('[AIChatPanel] Failed to share chat:', error);
      showMessage?.(error.message || 'Could not copy a share link.', 'danger');
    }
  };

  const sendMessage = async (messageText) => {
    const userMessage = messageText.trim();
    if (!userMessage || isLoading) return;

    userActedRef.current = true;
    isLoadingRef.current = true;
    stickToBottomRef.current = true;
    awaitingAckRef.current = true;
    transcriptEpochRef.current += 1;
    const nextMessages = [...messagesRef.current, { role: 'user', content: userMessage }];
    messagesRef.current = nextMessages;
    setInput('');
    setMessages(nextMessages);
    setIsLoading(true);

    try {
      const response = await fetchWithAuthTimeout('/api/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: userMessage,
          account_id: accountId,
          paper_trading: paperTrading,
          session_id: sessionId,
          stream: true,
          active_symbol: selectedTicker || null,
          current_price: Number.isFinite(currentPrice) ? currentPrice : null,
          atr: Number.isFinite(atrDistance) ? atrDistance : null,
          point_value: Number.isFinite(pointValue) && pointValue > 0 ? pointValue : null,
        }),
      });

      if (isEventStreamResponse(response)) {
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
        let streamed = '';
        await consumeSseResponse(response, {
          onEvent: (event) => {
            const eventSessionId = chatStreamEventSessionId(event);
            if (eventSessionId != null) {
              writeActiveSessionId(eventSessionId);
              sessionIdRef.current = eventSessionId;
              setSessionId(eventSessionId);
            }
            if (event.done || event.status === 'idle') {
              return;
            }
            streamed = chatStreamEventContent(event, streamed);
            applyPartial(streamed);
          },
        });
        if (streamed) {
          awaitingAckRef.current = false;
          setMessages((prev) => mergeMessagesWithPartial(
            applyPartialToMessages(prev, streamed),
            '',
            { processing: false },
          ));
          isLoadingRef.current = false;
          setIsLoading(false);
        }
        return;
      }

      const data = await response.json().catch(() => ({}));

      if (response.status === 409 && data.session_id) {
        awaitingAckRef.current = false;
        transcriptEpochRef.current += 1;
        setInput(userMessage);
        const inProgress = await fetchSession(data.session_id);
        if (inProgress) adoptSession(inProgress);
        else {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }

      if (data.session_id) {
        writeActiveSessionId(data.session_id);
        sessionIdRef.current = data.session_id;
        setSessionId(data.session_id);
      }

      // Older servers answered in this response. The current server only acks,
      // and the reply arrives through Pusher or the session poll.
      if (data.response) {
        isLoadingRef.current = false;
        setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[AIChatPanel] Error:', error);
      const isTimeout = error.name === 'AbortError';
      if (isTimeout) {
        try {
          const listRes = await fetchWithAuth('/api/chat/sessions');
          if (listRes.ok) {
            const list = await listRes.json();
            const processing = (Array.isArray(list) ? list : []).find((session) => isSessionProcessing(session));
            if (processing) {
              const session = await fetchSession(processing.id);
              if (session) {
                adoptSession(session);
                return;
              }
            }
          }
        } catch (recoverError) {
          console.error('[AIChatPanel] Failed to recover in-progress chat:', recoverError);
        }
      }
      awaitingAckRef.current = false;
      transcriptEpochRef.current += 1;
      isLoadingRef.current = false;
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: isTimeout
          ? 'The request timed out. Your message was saved — please try again.'
          : 'Sorry, I encountered an error. Please try again.',
      }]);
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    await sendMessage(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) handleSubmit(e);
  };

  const renderHistoryPanel = (showBackButton) => (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 12px', borderBottom: `1px solid ${colors.borderLight}`, flexShrink: 0,
      }}>
        {showBackButton && (
          <button
            type="button"
            onClick={closeHistory}
            style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '2px 4px', display: 'flex' }}
          >
            <ArrowLeft size={16} />
          </button>
        )}
        <span style={{ color: colors.text, fontSize: '14px', fontWeight: '600', flex: 1 }}>Previous Chats</span>
        <button
          type="button"
          onClick={startNewChat}
          style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 4px' }}
        >
          <PlusCircle size={14} /> New Chat
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '4px 6px 10px' }}>
        {sessionsLoading && (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <Spinner animation="border" size="sm" style={{ color: colors.primary }} />
          </div>
        )}
        {!sessionsLoading && sessions.length === 0 && (
          <div style={{ padding: '24px 16px', color: colors.textMuted, fontSize: '14px', textAlign: 'center' }}>
            No previous chats yet.
          </div>
        )}
        {!sessionsLoading && sessionGroups.map((group) => (
          <div key={group.key}>
            <div
              style={{
                padding: '10px 8px 4px',
                color: colors.textMuted,
                fontSize: '10px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {group.label}
            </div>
            {group.sessions.map((session) => {
              const isCurrent = session.id === sessionId;
              const showMeta = isMobile || activeSessionRow === session.id;
              return (
                <div
                  key={session.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => loadSession(session)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      loadSession(session);
                    }
                  }}
                  onMouseEnter={() => setActiveSessionRow(session.id)}
                  onMouseLeave={() => setActiveSessionRow((id) => (id === session.id ? null : id))}
                  // Focus bubbles from the delete button too, so keyboard users get the
                  // same reveal a pointer does.
                  onFocus={() => setActiveSessionRow(session.id)}
                  onBlur={() => setActiveSessionRow((id) => (id === session.id ? null : id))}
                  title={session.title}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '7px 8px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: isCurrent
                      ? `${colors.primary}2E`
                      : activeSessionRow === session.id ? `${colors.primary}1A` : 'transparent',
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      color: colors.text,
                      fontSize: '13px',
                      fontWeight: isCurrent ? '600' : '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {session.title}
                  </div>
                  {isSessionProcessing(session) && (
                    <span
                      data-testid={`chat-session-working-${session.id}`}
                      style={{
                        flexShrink: 0,
                        color: colors.primary,
                        fontSize: '10px',
                        fontWeight: '600',
                      }}
                    >
                      Working…
                    </span>
                  )}
                  {showMeta && (
                    <span
                      data-testid={`chat-session-meta-${session.id}`}
                      style={{
                        flexShrink: 0,
                        color: colors.textMuted,
                        fontSize: '10px',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatSessionMeta(session)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => shareSessionFromHistory(e, session)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      padding: '2px',
                      flexShrink: 0,
                      display: 'flex',
                      visibility: showMeta ? 'visible' : 'hidden',
                    }}
                    title="Copy share link"
                    aria-label={`Share ${session.title}`}
                  >
                    <Share size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => deleteSession(e, session.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      padding: '2px',
                      flexShrink: 0,
                      display: 'flex',
                      // Kept in layout so revealing it never nudges the title.
                      visibility: showMeta ? 'visible' : 'hidden',
                    }}
                    title="Delete chat"
                    aria-label={`Delete ${session.title}`}
                  >
                    <Trash size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  // ── Full-screen history (mobile / sidebar tab) ────────────────────────────
  if (view === 'history' && !useDesktopHistorySidebar) {
    return renderHistoryPanel(true);
  }

  // ── Chat view ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex',
      flex: 1,
      minHeight: 0,
      backgroundColor: chatCanvasBackground(colors.bgPrimary),
    }}
    >
      {useDesktopHistorySidebar ? (
        <aside
          data-testid="precision-ai-history-sidebar"
          style={{
            width: HISTORY_SIDEBAR_WIDTH,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRight: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgSecondary,
          }}
        >
          {renderHistoryPanel(false)}
        </aside>
      ) : null}
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
      {/* Toolbar — match history header padding and New Chat styling */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px', borderBottom: `1px solid ${colors.borderLight}`,
        flexShrink: 0,
      }}>
        {useDesktopHistorySidebar ? (
          <span style={{ color: colors.textMuted, fontSize: 14, fontWeight: 600 }}>PrecisionAI</span>
        ) : (
          <button
            type="button"
            onClick={openHistory}
            title="Previous chats"
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px',
              padding: '2px 4px',
            }}
          >
            <ClockHistory size={14} /> History
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={shareCurrentChat}
            disabled={!messages.some((message) => message.content)}
            data-testid="ai-chat-share"
            title="Copy share link"
            aria-label="Copy share link"
            style={{
              background: 'none',
              border: 'none',
              color: messages.some((message) => message.content) ? colors.textMuted : `${colors.textMuted}88`,
              cursor: messages.some((message) => message.content) ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px',
              padding: '2px 4px',
            }}
          >
            {shareCopied ? <Check size={14} /> : <Share size={14} />} {shareCopied ? 'Copied' : 'Share'}
          </button>
          <button
            onClick={startNewChat}
            title="New chat"
            style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', padding: '2px 4px' }}
          >
            <PlusCircle size={14} /> New Chat
          </button>
        </div>
      </div>

      {/* Messages — outer scrolls; inner stacks top-down (flex-end on scroll parent breaks history) */}
      <div
        ref={messagesScrollRef}
        onScroll={handleMessagesScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '12px',
            minHeight: messages.length === 0 && !isLoading ? '100%' : 'min-content',
            justifyContent: messages.length === 0 && !isLoading ? 'center' : 'flex-start',
            boxSizing: 'border-box',
            ...(pageContentStyle || {}),
          }}
        >
        {hydrating && messages.length === 0 && !isLoading && (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <Spinner animation="border" size="sm" style={{ color: colors.primary }} />
          </div>
        )}
        {messages.length === 0 && !isLoading && !hydrating && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px 4px',
              alignSelf: 'stretch',
            }}
          >
            <p
              style={{
                margin: '0 0 16px',
                fontSize: '18px',
                fontWeight: 600,
                color: colors.text,
                textAlign: 'center',
              }}
            >
              Try asking about
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                width: '100%',
                maxWidth: '360px',
              }}
            >
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => sendMessage(prompt)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    fontSize: `${AI_CHAT_FONT_SIZE}px`,
                    lineHeight: AI_CHAT_LINE_HEIGHT,
                    color: colors.text,
                    backgroundColor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.backgroundColor = `${colors.primary}12`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.backgroundColor = colors.bgCard;
                  }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
        <AIChatTranscript
          messages={messages}
          isLoading={isLoading}
          markdownComponents={markdownComponents}
          colors={colors}
        />
        {isLoading && !messages.some((message) => message.streaming && message.content) && (
          <div
            data-testid="ai-chat-pending"
            style={{
            alignSelf: 'flex-start',
            backgroundColor: assistantBubbleColors(colors).backgroundColor,
            border: assistantBubbleColors(colors).border,
            padding: '14px 16px',
            borderRadius: '12px',
            borderBottomLeftRadius: '4px',
            color: colors.textMuted,
            fontSize: `${AI_CHAT_FONT_SIZE}px`,
            lineHeight: String(AI_CHAT_LINE_HEIGHT),
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <Spinner animation="border" size="sm" />
            <span>{LOADING_PHRASES[loadingPhraseIndex]}</span>
          </div>
        )}
        <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input — pinned at bottom */}
      <Form onSubmit={handleSubmit} style={{
        padding: pageLayout ? '8px 0' : '8px 10px',
        borderTop: `1px solid ${colors.borderLight}`,
        flexShrink: 0,
      }}>
        <div style={pageContentStyle || undefined}>
        {!accountId && (
          <p style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '5px' }}>
            Select a trading account for context-aware responses.
          </p>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr min-content',
            columnGap: '6px',
            rowGap: '3px',
            alignItems: 'end',
          }}
        >
          <Form.Control
            ref={inputRef}
            as="textarea"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Message to PrecisionAI"
            disabled={isLoading}
            style={{
              gridColumn: 1,
              gridRow: 1,
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.borderLight}`,
              color: colors.white,
              resize: 'none',
              fontSize: `${AI_CHAT_FONT_SIZE}px`,
              lineHeight: String(AI_CHAT_LINE_HEIGHT),
              padding: '7px 9px',
              minWidth: 0,
            }}
            className="ai-chat-input"
          />
          <Button
            type="submit"
            className="ai-chat-submit-btn"
            disabled={!input.trim() || isLoading}
            style={{
              gridColumn: 2,
              gridRow: 1,
              backgroundColor: input.trim() ? colors.primary : colors.bgSecondary,
              border: `1px solid ${input.trim() ? colors.primary : colors.borderLight}`,
              color: colors.white,
              transition: 'background-color 0.15s ease, border-color 0.15s ease',
            }}
          >
            <ArrowUp size={14} />
          </Button>
          <div
            style={{
              gridColumn: 1,
              gridRow: 2,
              fontSize: '12px',
              color: colors.textMuted,
              textAlign: 'right',
              lineHeight: 1.2,
              paddingTop: '4px',
            }}
          >
            Enter to send
          </div>
        </div>
        </div>
      </Form>
    </div>
    </div>
  );
};

export default AIChatPanel;
